import {
  type NilDealResponsePagedData,
  type PublicPlayerDealResponse,
  v2DealsList,
} from '@on3/api';
import type { ErrorData } from '@on3/ui-lib/api/schema/custom-contracts';
import { Check } from '@on3/ui-lib/components/Svg';
import { useSite } from '@on3/ui-lib/index';
import { externalApi } from '@on3/ui-lib/utils/api';
import { type AxiosError } from 'axios';
import { AgentIcon } from 'components/Icons/AgentIcon';
import { InternalMenu, NilInternalMenu } from 'components/Menu/InternalMenu';
import { Twitter } from 'components/Svg/Twitter';
import { PrimaryButton } from 'components/UI/Buttons/PrimaryButton';
import { TextLink } from 'components/UI/Link/TextLink';
import { Text } from 'components/UI/Typography/Text';
import { useCallback, useMemo, useState } from 'react';

import { DealTrackerItem } from './DealTrackerItems/DealTrackerItem';
import styles from './NilDealTracker.module.scss';
import { NilDealTrackerHeader } from './NIlDealTrackerHeader';

const arr: [] = [];

interface NilDealTrackerProps {
  title: string;
  deals: NilDealResponsePagedData;
}

export const NilDealTracker = ({ deals, title }: NilDealTrackerProps) => {
  const { pagination, list } = deals;
  const [disable, setDisable] = useState(false);
  const [pageNumber, setPageNumber] = useState(2);
  const { currentSite } = useSite();
  const siteColor = currentSite?.linkColor;
  const page = pageNumber;
  const [dealList, setDealList] = useState<PublicPlayerDealResponse[]>(
    list ?? arr,
  );
  const primaryBackgroundStyle = useMemo(() => {
    return { backgroundColor: siteColor || '#0D8BFF' };
  }, [siteColor]);

  const showLoadMoreButton =
    pagination?.count &&
    pagination?.count > 25 &&
    dealList?.length < pagination?.count;

  const handleClick: React.MouseEventHandler = useCallback(async () => {
    setDisable(true);
    const params = {
      page,
    };

    try {
      await v2DealsList(externalApi, params)
        .then((res) => {
          if (res.list) {
            setDealList([...dealList, ...res.list]);
          } else {
            console.error('Response list is undefined');
          }

          setPageNumber(+pageNumber + 1);
        })
        .catch((err) => {
          console.log(err);
        });
    } catch (e: unknown) {
      const { response, message }: AxiosError<ErrorData> =
        e as AxiosError<ErrorData>;

      console.error(
        'NIL Deal Tracker Error: ',
        response?.data?.message || message,
      );
    } finally {
      setDisable(false);
    }
  }, [dealList, page, pageNumber]);

  return (
    <div className={styles.trackerContainer}>
      <header className={styles.header}>
        <div className={styles.titleContainer}>
          <Text className={styles.headerText} variant="h1">
            {title}
          </Text>
          <TextLink
            className={styles.twitterLink}
            href="https://twitter.com/deals_nil"
            rel="noreferrer"
            target="_blank"
          >
            <Twitter
              className={styles.twitterIcon}
              fill={siteColor || '#000'}
            />
            @deals_nil
          </TextLink>
        </div>
        <TextLink
          className={styles.reportLink}
          href="mailto:nil@on3.com?subject=Report%20NIL%20Deal&body=I%20would%20like%20to%20report%20a%20deal%20for"
          style={primaryBackgroundStyle}
          title="Report an NIL deal"
          underline="none"
        >
          Report a Deal
        </TextLink>

        <Text>
          The On3 NIL Deal Tracker lists all NIL Deals across the NCAA for
          college and high school athletes being reported by players,
          collectives, agents and media. On3 does NOT disclose deal financial
          terms. Financial data is undisclosed and private to the athlete.
        </Text>
      </header>
      <InternalMenu list={NilInternalMenu} />
      <NilDealTrackerHeader />

      <ul className={styles.dealTrackerList}>
        {dealList?.map((item) => (
          <DealTrackerItem item={item} key={item?.key} />
        ))}
      </ul>

      {showLoadMoreButton && (
        <PrimaryButton
          className={styles.btnLoadMore}
          disabled={disable}
          fullWidth={false}
          onClick={handleClick}
          variant="text"
        >
          Load More
        </PrimaryButton>
      )}
      <ul className={styles.legendContainer}>
        <li className={styles.legendItem}>
          <div className={styles.checkmarkContainer}>
            <Check className={styles.checkmarkIcon} />
          </div>
          <Text className={styles.verified}>
            Verified: Deal contract has been reviewed by On3/NIL
          </Text>
        </li>
        <li className={styles.legendItem}>
          <AgentIcon className={styles.agentIcon} />
          <Text className={styles.agentDescription}>Agent or Agency</Text>
        </li>
      </ul>
    </div>
  );
};
